// Primary top-nav items with a real overflow menu.
//
// THE BUG THIS FIXES (Grant, 2026-07-25): "the top menu items are not all
// legible and I do not see a way to access all of the menu items."
//
// The list was `overflow-x: auto` with `scrollbar-width: none`. So when the
// items didn't fit — a narrower window, a zoomed browser, or the Ask Stew
// drawer open alongside — the last item was sliced mid-word ("PROC…") and
// the ones past it were reachable ONLY by an invisible horizontal scroll.
// With a mouse that is no scrollbar, no arrows, no hint: the destinations
// were effectively gone. (An earlier pass swapped overflow:hidden → auto to
// stop the hard clip, but hiding the scrollbar kept them unreachable.)
//
// Now: measure what actually fits and move the remainder into a "More"
// dropdown, so every primary destination is always ONE visible click away
// at any width. Nothing is ever half-rendered.

import { ChevronDown } from "lucide-react";
import {
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { Link } from "react-router-dom";

import { CoreVocabularyTip } from "./CoreVocabularyTip";
import { navItemsSignature, visibleNavCount, type NavItem } from "./top-nav-helpers";

/** Fallback for the flex `gap` between items, used only if the real gap
 *  cannot be read. The real value is READ from .top-nav-list (see
 *  readItemGap): Lane 1.1 moved that gap from 4px to 20px and this constant
 *  did not follow, so the bar thought more items fit than do and "More"
 *  landed under the search box (the staging audit, 2026-11-04). */
const FALLBACK_ITEM_GAP_PX = 4;

/** The gap between items as the browser actually lays it out. */
function readItemGap(ul: HTMLElement): number {
  const raw = getComputedStyle(ul).columnGap;
  const px = Number.parseFloat(raw);
  return Number.isFinite(px) ? px : FALLBACK_ITEM_GAP_PX;
}
/** Width to reserve for the "More" trigger when it has to render. */
const MORE_TRIGGER_PX = 104;

interface TopNavPrimaryNavProps {
  items: NavItem[];
  activeTo: string | null;
}

export function TopNavPrimaryNav({ items, activeTo }: TopNavPrimaryNavProps) {
  const navRef = useRef<HTMLElement | null>(null);
  const listRef = useRef<HTMLUListElement | null>(null);
  // Natural width of every item, measured once while they are all rendered.
  // Cached so later resizes recompute without needing them back in the DOM.
  const widthsRef = useRef<number[]>([]);
  const [visibleCount, setVisibleCount] = useState(items.length);
  const [moreOpen, setMoreOpen] = useState(false);
  const moreWrapRef = useRef<HTMLDivElement | null>(null);
  const moreBtnRef = useRef<HTMLButtonElement | null>(null);
  const menuId = useId();

  const recompute = useCallback(() => {
    const nav = navRef.current;
    const ul = listRef.current;
    const widths = widthsRef.current;
    if (!nav || !ul || widths.length === 0) return;
    // The gap is read NOW, not when the widths were measured: a container
    // query narrows it at small widths, and a width measured at one gap but
    // fitted at another is the bug this replaced.
    const gap = readItemGap(ul);
    // The fitting math lives in the JSX-free helpers module so it's
    // unit-testable without rendering (see visibleNavCount).
    setVisibleCount(
      visibleNavCount(
        widths.map((w) => w + gap),
        nav.clientWidth,
        MORE_TRIGGER_PX,
      ),
    );
  }, []);

  // Re-measure from scratch whenever the item set changes (role switch,
  // impersonation) — cached widths from a different set would be wrong.
  // Keyed on navItemsSignature(), NOT on `items` array identity (Sentry
  // JAVASCRIPT-REACT-3N, 2026-08-27): a caller that hands in a fresh
  // array with the same items every render — as TopNav did before it
  // was memoized — made this effect fire on every render, clearing
  // widthsRef right before the measurement effect below re-populated
  // it, which called recompute() and changed visibleCount, forever.
  // The signature is `===`-stable across renders whenever the actual
  // items are unchanged, so a future caller passing an inline array
  // can't reintroduce the loop.
  useLayoutEffect(() => {
    widthsRef.current = [];
    setVisibleCount(items.length);
  }, [navItemsSignature(items)]);

  // Measure once, while every item is in the DOM. Intentionally has no dep
  // array: it must run after the render that shows them all. It self-guards
  // (widths already cached → no-op), so it can't loop.
  useLayoutEffect(() => {
    const ul = listRef.current;
    if (!ul || widthsRef.current.length > 0) return;
    const nodes = Array.from(
      ul.querySelectorAll<HTMLElement>("[data-nav-item]"),
    );
    if (nodes.length !== items.length || nodes.length === 0) return;
    // Natural widths only; the gap is added when fitting (see recompute).
    const widths = nodes.map((n) => n.getBoundingClientRect().width);
    if (widths.some((w) => w <= 0)) return; // not laid out yet
    widthsRef.current = widths;
    recompute();
  });

  useEffect(() => {
    const nav = navRef.current;
    if (!nav || typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(() => recompute());
    ro.observe(nav);
    return () => ro.disconnect();
  }, [recompute]);

  // Dismiss the overflow menu on outside click / Escape, mirroring the
  // notifications dropdown's behavior.
  useEffect(() => {
    if (!moreOpen) return;
    function onMouseDown(event: MouseEvent) {
      const target = event.target as Node | null;
      if (target && moreWrapRef.current?.contains(target)) return;
      setMoreOpen(false);
    }
    function onKey(event: KeyboardEvent) {
      if (event.key === "Escape") {
        setMoreOpen(false);
        moreBtnRef.current?.focus();
      }
    }
    document.addEventListener("mousedown", onMouseDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onMouseDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [moreOpen]);

  const shown = items.slice(0, visibleCount);
  const overflow = items.slice(visibleCount);
  // If an item in the overflow set is the active page, say so on the
  // trigger — otherwise the current location looks unhighlighted.
  const activeIsHidden = overflow.some((i) => i.to === activeTo);

  return (
    <nav aria-label="Primary" className="top-nav-primary" ref={navRef}>
      <ul className="top-nav-list" ref={listRef}>
        {shown.map((item) => {
          const isActive = activeTo === item.to;
          return (
            <li key={item.to} className="top-nav-item" data-nav-item>
              <Link
                to={item.to}
                className={`top-nav-link${isActive ? " is-active" : ""}`}
                aria-current={isActive ? "page" : undefined}
              >
                {item.label}
              </Link>
              {/* Inline "what is this?" help (sibling of the link, so the
                  `?` never navigates). Nothing renders without a help key. */}
              {item.help && <CoreVocabularyTip k={item.help} />}
            </li>
          );
        })}
      </ul>

      {overflow.length > 0 && (
        <div className="top-nav-more" ref={moreWrapRef}>
          <button
            type="button"
            ref={moreBtnRef}
            className={`top-nav-link top-nav-more__trigger${
              activeIsHidden ? " is-active" : ""
            }`}
            aria-haspopup="true"
            aria-expanded={moreOpen}
            aria-controls={moreOpen ? menuId : undefined}
            onClick={() => setMoreOpen((v) => !v)}
          >
            More
            <ChevronDown size={13} strokeWidth={2.25} aria-hidden="true" />
            <span className="visually-hidden">
              {` (${overflow.length} more navigation ${
                overflow.length === 1 ? "item" : "items"
              })`}
            </span>
          </button>
          {moreOpen && (
            <ul className="top-nav-more__menu" id={menuId}>
              {overflow.map((item) => {
                const isActive = activeTo === item.to;
                return (
                  <li key={item.to}>
                    <Link
                      to={item.to}
                      className={`top-nav-more__link${
                        isActive ? " is-active" : ""
                      }`}
                      aria-current={isActive ? "page" : undefined}
                      onClick={() => setMoreOpen(false)}
                    >
                      {item.label}
                    </Link>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}
    </nav>
  );
}
